import React, { useRef, useEffect, useState, useCallback } from 'react';
import { WithTranslationsProps } from 'react-utilities';
import { Loading } from 'react-style-guide';
import { jsClientDeviceIdentifier } from 'header-scripts';
import { eventStreamService } from 'core-roblox-utilities';
import { TGameData, TMetaData } from '../common/types/bedev1Types';
import bedev1Services from '../common/services/bedev1Services';
import { queryParams } from '../common/constants/browserConstants';
import { GameGrid } from '../common/components/GameGrid';
import { IEGameGrid } from '../common/components/IEGameGrid';
import { TOnTileClick } from '../common/components/GameTileUtils';
import { gameSearch } from '../common/constants/configConstants';
import eventStreamConstants, { PageContext } from '../common/constants/eventStreamConstants';
import { FeatureGamePage } from '../common/constants/translationConstants';
import searchHelper from '../../../js/react/gameSearch/utils/searchHelper';
import { getSession, buildGameDetailUrl } from '../common/utils/browserUtils';
import {
  newGrid,
  IEGrid,
  useGameImpressionGridTracker
} from '../common/hooks/useGameImpressionGridTracker';

function GameSearch({ translate }: WithTranslationsProps): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const keyword = searchHelper.getKeyword() as string;
  const session = getSession(queryParams.gameSearchQuerySession);
  const [gameData, setGameData] = useState<TGameData[] | undefined>(undefined);
  const [metaData, setMetaData] = useState<TMetaData | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [index, setIndex] = useState<number>(0);

  useGameImpressionGridTracker(
    tileRef,
    gameData,
    metaData,
    keyword,
    session,
    jsClientDeviceIdentifier.isIE ? IEGrid.calculateViewedGameData : newGrid.calculateViewedGameData
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { games, metaData: data } = await bedev1Services.getGameList(
        keyword,
        index,
        gameSearch.numberOfGameTilesPerLoad
      );
      setGameData([...(gameData || []), ...games]);
      setIndex(index + gameSearch.numberOfGameTilesPerLoad);
      setMetaData(Object.assign(data, metaData));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [keyword, gameData, metaData, index, setMetaData, setIndex]);

  const onScroll = useCallback(() => {
    const gridOffset = (gridRef.current?.offsetTop || 0) + (gridRef.current?.clientHeight || 0);
    const windowOffset = window.pageYOffset + window.innerHeight;
    if (windowOffset > gridOffset && metaData?.hasMoreRows === false) {
      window.removeEventListener('scroll', onScroll);
    } else if (windowOffset > gridOffset && metaData?.hasMoreRows === true && loading === false) {
      // eslint-disable-next-line no-void
      void loadData();
    }
  }, [metaData?.hasMoreRows, loading, loadData]);

  const onTileClick = useCallback<TOnTileClick>(
    (data, id) => {
      eventStreamService.sendEvent(
        ...((eventStreamConstants.gameDetailReferral(
          data.placeId,
          data.isSponsored ? 1 : 0,
          id,
          PageContext.searchPage,
          gameData?.length || 0,
          session
        ) as unknown) as Parameters<typeof eventStreamService.sendEvent>)
      );
      window.open(
        buildGameDetailUrl(data.placeId, data.name, {
          [queryParams.gameSearchQuerySession]: session
        }),
        '_self'
      );
    },
    [gameData?.length]
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useEffect(() => {
    // eslint-disable-next-line no-void
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (gameData === undefined || metaData === undefined) {
    return <Loading />;
  }
  if (gameData.length === 0) {
    return <div className='font-bold'>{translate(FeatureGamePage.LabelNoSearchResults)}</div>;
  }

  return (
    <React.Fragment>
      {jsClientDeviceIdentifier.isIE ? (
        <IEGameGrid
          ref={gridRef}
          tileRef={tileRef}
          gameData={gameData}
          metaData={metaData}
          translate={translate}
          onTileClick={onTileClick}
        />
      ) : (
        <GameGrid
          ref={gridRef}
          tileRef={tileRef}
          gameData={gameData}
          metaData={metaData}
          translate={translate}
          onTileClick={onTileClick}
        />
      )}
      {loading && <Loading />}
    </React.Fragment>
  );
}

export default GameSearch;
